import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card } from '@/components/ui';
import { apiFetch } from '@/lib/api/client';
import { paiseToRupees, humanDuration, type PlatformSetting } from '@makemypartner/shared';

/**
 * Where the platform's business values are changed — fees, minimums, hold
 * periods, refund rules.
 *
 * This screen is the whole point of keeping these in the database rather than
 * `.env`: raising the platform fee should be something the founder does on a
 * Tuesday afternoon, not a code change, a review, a deploy and a restart.
 *
 * project.md §48.
 */

/** Groups, so fourteen rows read as four decisions rather than a wall. */
const GROUPS: { prefix: string; title: string; note: string }[] = [
  { prefix: 'fee.', title: 'Platform fees', note: 'Charged once, per account.' },
  {
    prefix: 'revenue.',
    title: 'Revenue',
    note: 'What companions keep, and what a coin is worth when they cash out.',
  },
  {
    prefix: 'withdrawal.',
    title: 'Withdrawals',
    note: 'When companions can take their earnings out.',
  },
  {
    prefix: 'cancellation.',
    title: 'Cancellations',
    note: 'What a user gets back, and when.',
  },
  { prefix: 'booking.', title: 'Bookings', note: 'Limits on how bookings are made.' },
  { prefix: 'wallet.', title: 'Wallet', note: 'Top-up limits and warnings.' },
];

export default function AdminSettings() {
  const [settings, setSettings] = useState<PlatformSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch<{ settings: PlatformSetting[] }>('/api/admin/settings');
      setSettings(res.settings);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load settings.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <header className="animate-rise mb-8">
        <p className="eyebrow mb-2">Admin</p>
        <h1 className="text-3xl font-semibold">Platform settings</h1>
        <p className="mt-2 text-[var(--ink-2)]">
          Changes take effect within a minute. They apply to new transactions only — nothing that
          has already happened is recalculated.
        </p>
      </header>

      {error && (
        <Card className="mb-6 px-5 py-4">
          <p className="text-sm" style={{ color: 'var(--danger)' }}>
            {error}
          </p>
        </Card>
      )}

      {loading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-28 animate-pulse rounded-2xl"
              style={{ background: 'var(--surface-2)' }}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          {GROUPS.map((g) => {
            const rows = settings.filter((s) => s.key.startsWith(g.prefix));
            if (rows.length === 0) return null;

            return (
              <section key={g.prefix}>
                <div className="mb-3">
                  <h2 className="text-lg font-semibold">{g.title}</h2>
                  <p className="text-sm text-[var(--ink-2)]">{g.note}</p>
                </div>
                <div className="flex flex-col gap-3">
                  {rows.map((s) => (
                    <SettingRow key={s.key} setting={s} onSaved={load} onError={setError} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Show a raw number in the unit a person thinks in.
 *
 * 19900 is not a fee anyone can sanity-check; "₹199" is. A number nobody can
 * read is a number nobody will notice is wrong.
 */
function displayValue(s: PlatformSetting): string {
  if (typeof s.value === 'boolean') return s.value ? 'Yes' : 'No';
  if (typeof s.value !== 'number') return String(s.value);

  switch (s.unit) {
    case 'paise':
      return paiseToRupees(s.value);
    case 'seconds':
      return humanDuration(s.value);
    case 'percent':
      return `${s.value}%`;
    case 'minutes':
      return `${s.value} min`;
    case 'coins':
      return `${s.value} coins`;
    default:
      return String(s.value);
  }
}

function SettingRow({
  setting,
  onSaved,
  onError,
}: {
  setting: PlatformSetting;
  onSaved: () => Promise<void>;
  onError: (m: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(setting.value));
  const [busy, setBusy] = useState(false);

  const isBool = setting.kind === 'bool';
  const n = Number(draft);
  const outOfRange =
    setting.kind === 'int' &&
    ((setting.minValue !== null && n < setting.minValue) ||
      (setting.maxValue !== null && n > setting.maxValue));

  async function save(value: number | string | boolean) {
    setBusy(true);
    onError(null);
    try {
      await apiFetch(`/api/admin/settings/${setting.key}`, {
        method: 'PATCH',
        body: { value },
      });
      await onSaved();
      setEditing(false);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not save.');
    }
    setBusy(false);
  }

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold">{setting.label}</h3>
            {setting.isPublic && <Badge tone="neutral">Shown publicly</Badge>}
          </div>
          <p className="mt-1 text-sm leading-relaxed text-[var(--ink-2)]">{setting.description}</p>
          <code className="mt-2 block text-xs text-[var(--muted)]">{setting.key}</code>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          {isBool ? (
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => void save(!(setting.value as boolean))}
            >
              {displayValue(setting)}
            </Button>
          ) : editing ? (
            <div className="flex items-center gap-2">
              <input
                autoFocus
                type="number"
                className="w-28 rounded-lg px-3 py-2 text-sm tabular-nums outline-none"
                style={{
                  background: 'var(--surface)',
                  color: 'var(--ink)',
                  border: `1px solid ${outOfRange ? 'var(--danger)' : 'var(--line)'}`,
                }}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
              <Button size="sm" disabled={busy || outOfRange} onClick={() => void save(n)}>
                Save
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setDraft(String(setting.value));
                  setEditing(false);
                }}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <>
              <span className="text-lg font-semibold tabular-nums">{displayValue(setting)}</span>
              <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
                Change
              </Button>
            </>
          )}
        </div>
      </div>

      {editing && setting.unit && (
        <p className="mt-3 text-xs text-[var(--muted)]">
          Entered in <strong>{setting.unit}</strong>
          {setting.minValue !== null && setting.maxValue !== null && (
            <>
              {' '}
              · allowed {setting.minValue}–{setting.maxValue}
            </>
          )}
          {setting.unit === 'paise' && ' · 100 paise = ₹1'}
        </p>
      )}
    </Card>
  );
}
